/**
 * Physical displays and the perform-mode URL.
 *
 * `<gr-output>` is the page's Window COMP: the block previews its canvas
 * inline, fills this window, sends pixels to a chosen display, and
 * `?output=<name>` opens the page straight into that surface (perform
 * mode) — the whole page still runs, so the bus and hardware stay live.
 * These helpers are the DOM-free part: display enumeration via the
 * Window Management API and the URL convention.
 */

export const OUTPUT_PARAM = "output";

export function readOutputParam(search: string): string | null {
  const raw = new URLSearchParams(search).get(OUTPUT_PARAM);
  const value = raw?.trim();
  return value ? value : null;
}

/** Same page, straight into one output. Every other query param survives. */
export function buildOutputUrl(href: string, name: string): string {
  const url = new URL(href);
  url.searchParams.set(OUTPUT_PARAM, name);
  return url.toString();
}

export function parseScreenIndex(value: string | null): number | null {
  const index = Number(value);
  if (value === null || value.trim() === "" || !Number.isInteger(index) || index < 0) return null;
  return index;
}

export interface ScreenTarget {
  index: number;
  label: string;
  left: number;
  top: number;
  width: number;
  height: number;
  isPrimary: boolean;
  /** The display this tab is already on — fullscreen, don't open a copy. */
  isCurrent: boolean;
}

interface ScreenDetailed {
  label?: string;
  isPrimary?: boolean;
  availLeft?: number;
  availTop?: number;
  availWidth?: number;
  availHeight?: number;
  left: number;
  top: number;
  width: number;
  height: number;
}

interface WindowWithScreenDetails extends Window {
  getScreenDetails?: () => Promise<{
    screens: ScreenDetailed[];
    currentScreen?: ScreenDetailed;
  }>;
}

interface ScreenWithOffsets extends Screen {
  availLeft?: number;
  availTop?: number;
}

let cached: ScreenTarget[] | null = null;

/** Displays from the last successful `listScreens()`, for gesture-critical paths. */
export function cachedScreenTargets(): ScreenTarget[] | null {
  return cached;
}

/**
 * Physical displays via the Window Management API, falling back to the
 * current one so callers always get at least a placeable target. The
 * permission prompt needs a user gesture on first use.
 */
export async function listScreens(): Promise<ScreenTarget[]> {
  const getScreenDetails = (window as WindowWithScreenDetails).getScreenDetails;
  if (getScreenDetails) {
    try {
      const details = await getScreenDetails.call(window);
      const current = details.currentScreen;
      const targets = details.screens.map((screen, index) => ({
        index,
        label: screen.label && screen.label.length > 0 ? screen.label : `Display ${index + 1}`,
        left: screen.availLeft ?? screen.left,
        top: screen.availTop ?? screen.top,
        width: screen.availWidth ?? screen.width,
        height: screen.availHeight ?? screen.height,
        isPrimary: screen.isPrimary === true,
        isCurrent:
          screen === current ||
          (!!current &&
            (screen.availLeft ?? screen.left) === (current.availLeft ?? current.left) &&
            (screen.availTop ?? screen.top) === (current.availTop ?? current.top)),
      }));
      if (targets.length > 0) {
        cached = targets;
        return targets;
      }
    } catch {
      // Denied or unsupported — the single-display guess below still works.
    }
  }
  cached = [currentScreen()];
  return cached;
}

/** Only enumerate without a gesture when the permission is already granted. */
export async function silentScreenTargets(): Promise<ScreenTarget[] | null> {
  const existing = cachedScreenTargets();
  if (existing) return existing;
  try {
    const status = await navigator.permissions.query({
      name: "window-management" as PermissionName,
    });
    if (status.state !== "granted") return null;
  } catch {
    return null;
  }
  return listScreens();
}

function currentScreen(): ScreenTarget {
  const screen = window.screen as ScreenWithOffsets;
  return {
    index: 0,
    label: "This display",
    left: screen.availLeft ?? 0,
    top: screen.availTop ?? 0,
    width: screen.availWidth,
    height: screen.availHeight,
    isPrimary: true,
    isCurrent: true,
  };
}

/**
 * A second window is only needed when the picture has to leave this tab.
 * This display, or the only detected display, uses requestFullscreen instead.
 */
export function presentNeedsSurface(
  targets: ScreenTarget[],
  screenIndex: number | undefined,
): boolean {
  if (screenIndex === undefined) return true;
  const target = targets[screenIndex];
  if (!target) return true;
  return !target.isCurrent;
}
